import React from 'react'
import { Link } from 'react-router-dom'

import CareFlow from 'src/consumer/components/AumhumSite/CareFlow'
import { Container } from 'src/consumer/components/AumhumSite/sections'
import { getConditionBySlug } from 'src/consumer/components/AumhumSite/conditionsData'
import FaqSection from 'src/consumer/components/PrewallAndPaywall/FaqSection'
import { FAQ_LAYOUTS } from 'src/consumer/components/PrewallAndPaywall/faqComponents/utils'

import { SCORE_TONES, getAssessmentContent } from './assessmentsContentData'

/*
 * AssessmentLanding
 *
 * Replaces the old InfoAndSingleActionComponentVarOne +
 * InfoWithHeadingDescriptionAndImage + plan-cards landing that the
 * public AssessmentPlayer used to show before the quiz starts.
 *
 * The new version pulls researched, assessment-specific content from
 * `assessmentsContentData.jsx` and layers server-provided title /
 * description / FAQs / cover image on top. Unknown assessment slugs
 * degrade gracefully to the server data only.
 *
 * Primary CTA: "Begin the test" — triggers the quiz.
 * Secondary CTA: "Talk to Care Team" — opens the Connect-with-
 * our-team modal (same one the header pill uses).
 */

const ACCENT_TO_CLASS = {
  indigo: 'al-accent-indigo',
  violet: 'al-accent-violet',
  teal: 'al-accent-teal',
  amber: 'al-accent-amber',
  rose: 'al-accent-rose',
  sky: 'al-accent-sky',
  slate: 'al-accent-slate',
}

export default function AssessmentLanding({
  assessmentData,
  slug,
  onBegin,
  onContactClick,
}) {
  const content = getAssessmentContent(slug)
  const accentClass = ACCENT_TO_CLASS[content?.accent] || ACCENT_TO_CLASS.indigo

  const displayTitle = content?.shortName || assessmentData?.title || 'Self-assessment'
  const fullName = content?.fullName
  const measures = content?.measures || assessmentData?.shortDescription
  const overview =
    content?.overview && content.overview.length
      ? content.overview
      : assessmentData?.description
        ? [assessmentData.description]
        : []
  const items = content?.items
  const timeMin = content?.timeMin
  const validatedBy = content?.validatedBy
  const sampleItem = content?.sampleItem
  const scoreBands = content?.scoreBands || []
  const whenToTake = content?.whenToTake || []
  const whenNotEnough = content?.whenNotEnough
  const encouragement = content?.encouragement
  const relatedCondition = content?.relatedConditionSlug
    ? getConditionBySlug(content.relatedConditionSlug)
    : null

  const callContact = () => onContactClick?.({ mode: 'call' })
  const waContact = () => onContactClick?.({ mode: 'whatsapp' })

  return (
    <div className={`al-page ${accentClass}`}>
      {/* ── Hero ──────────────────────────────────────── */}
      <section className="al-hero">
        <Container>
          <div className="al-hero-grid">
            <div className="al-hero-copy">
              <div className="al-eyebrow">
                {content?.category
                  ? `${content.category} · Self-assessment`
                  : 'Self-assessment'}
              </div>
              <h1 className="al-h1">{displayTitle}</h1>
              {fullName ? <div className="al-full-name">{fullName}</div> : null}
              {measures ? <p className="al-lede">{measures}</p> : null}

              <ul className="al-meta-row">
                {items ? (
                  <li className="al-meta">
                    <MetaIcon kind="items" />
                    <span>{items} items</span>
                  </li>
                ) : null}
                {timeMin ? (
                  <li className="al-meta">
                    <MetaIcon kind="time" />
                    <span>~{timeMin} min</span>
                  </li>
                ) : null}
                <li className="al-meta">
                  <MetaIcon kind="lock" />
                  <span>Private & secure</span>
                </li>
                <li className="al-meta">
                  <MetaIcon kind="check" />
                  <span>Clinician-validated</span>
                </li>
              </ul>

              <div className="al-cta-row">
                <button
                  type="button"
                  className="al-cta al-cta-primary"
                  onClick={onBegin}
                >
                  Begin the test
                </button>
                <button
                  type="button"
                  className="al-cta al-cta-ghost"
                  onClick={callContact}
                >
                  Talk to Care Team
                </button>
              </div>
              {encouragement ? (
                <p className="al-encourage">{encouragement}</p>
              ) : null}
            </div>

            <aside className="al-hero-panel" aria-hidden="true">
              <div className="al-hero-panel-stamp">
                <PaperClipIcon />
              </div>
              <div className="al-hero-panel-title">This is a screener</div>
              <p className="al-hero-panel-body">
                Screeners help name what’s going on. They don’t diagnose. Any score
                that raises a question is reviewed by our psychiatry and psychology team
                before we draw conclusions.
              </p>
              {validatedBy ? (
                <div className="al-hero-panel-validated">
                  <div className="al-hero-panel-validated-label">Validated by</div>
                  <p>{validatedBy}</p>
                </div>
              ) : null}
            </aside>
          </div>
        </Container>
      </section>

      {/* ── Overview ──────────────────────────────────── */}
      {overview.length ? (
        <section className="al-section">
          <Container>
            <div className="al-section-grid">
              <div>
                <div className="al-section-eyebrow">About this screener</div>
                <h2 className="al-h2">What you’ll be measuring</h2>
              </div>
              <div className="al-prose">
                {overview.map((p, i) => (
                  <p key={`ov-${i}`}>{p}</p>
                ))}
              </div>
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── Sample item ───────────────────────────────── */}
      {sampleItem ? (
        <section className="al-section al-section-alt">
          <Container>
            <div className="al-sample-card">
              <div className="al-sample-eyebrow">A question like this</div>
              <blockquote className="al-sample-quote">
                <QuoteGlyph />
                <span>{sampleItem}</span>
              </blockquote>
              <div className="al-sample-note">
                Each item has four or five answer choices — pick whichever is closest to
                your honest experience. There’s no “right” answer.
              </div>
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── Score bands ───────────────────────────────── */}
      {scoreBands.length ? (
        <section className="al-section">
          <Container>
            <div className="al-section-grid">
              <div>
                <div className="al-section-eyebrow">What the score means</div>
                <h2 className="al-h2">Ranges, at a glance</h2>
                <p className="al-section-sub">
                  The final score is less important than the pattern it points to — but
                  here’s how clinicians read it.
                </p>
              </div>
              <ul className="al-bands">
                {scoreBands.map((b, i) => (
                  <li key={`band-${i}`} className="al-band">
                    <span
                      className="al-band-dot"
                      style={{ background: SCORE_TONES[b.tone] || SCORE_TONES.mod }}
                      aria-hidden="true"
                    />
                    <span className="al-band-range">{b.range}</span>
                    <span className="al-band-label">{b.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── When to take + when not enough ────────────── */}
      {(whenToTake.length || whenNotEnough) ? (
        <section className="al-section al-section-alt">
          <Container>
            <div className="al-two-col">
              {whenToTake.length ? (
                <div className="al-when-card">
                  <div className="al-section-eyebrow">When to take it</div>
                  <ul className="al-when-list">
                    {whenToTake.map((w, i) => (
                      <li key={`wt-${i}`}>
                        <Check />
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {whenNotEnough ? (
                <div className="al-when-card al-when-card-warn">
                  <div className="al-section-eyebrow">When a screener isn’t enough</div>
                  <p>{whenNotEnough}</p>
                  <div className="al-when-card-actions">
                    <button
                      type="button"
                      className="al-cta al-cta-primary al-cta-compact"
                      onClick={callContact}
                    >
                      Book a call today
                    </button>
                    <button
                      type="button"
                      className="al-cta al-cta-ghost al-cta-compact"
                      onClick={waContact}
                    >
                      Or message on WhatsApp
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── Care flow (the rest of the journey) ───────── */}
      <section className="al-section">
        <Container>
          <CareFlow
            title="After the test — here’s what happens"
            sub="Your score becomes the opening line of a clinical conversation, not an endpoint."
          />
        </Container>
      </section>

      {/* ── Related condition deep-dive ───────────────── */}
      {relatedCondition ? (
        <section className="al-section al-section-alt">
          <Container>
            <div className="al-related">
              <div>
                <div className="al-section-eyebrow">Go deeper</div>
                <h2 className="al-h2">
                  Learn about {relatedCondition.title.toLowerCase()}
                </h2>
                <p className="al-section-sub">
                  {relatedCondition.cardBlurb ||
                    `How ${relatedCondition.title.toLowerCase()} is recognised, assessed, and treated at AUMHUM.`}
                </p>
              </div>
              <div className="al-related-actions">
                <Link
                  to={`/conditions/${relatedCondition.slug}`}
                  className="al-cta al-cta-primary"
                >
                  Read the full page
                </Link>
              </div>
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── FAQ ───────────────────────────────────────── */}
      {assessmentData?.faqCategories?.length ? (
        <section className="al-section">
          <Container>
            <div className="al-section-grid">
              <div>
                <div className="al-section-eyebrow">Questions</div>
                <h2 className="al-h2">Before you start</h2>
              </div>
              <FaqSection
                categoryIds={assessmentData.faqCategories}
                layoutType={FAQ_LAYOUTS.TWO_COLUMN_SIDEBAR}
              />
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── Final CTA ─────────────────────────────────── */}
      <section className="al-section al-section-alt">
        <Container>
          <div className="al-final-cta">
            <h2 className="al-h2">Ready when you are.</h2>
            <p>
              The test takes {timeMin ? `about ${timeMin} minutes` : 'just a few minutes'}
              . If anything in the result worries you, we’re one tap away.
            </p>
            <div className="al-cta-row">
              <button
                type="button"
                className="al-cta al-cta-primary"
                onClick={onBegin}
              >
                Begin the test
              </button>
              <button
                type="button"
                className="al-cta al-cta-ghost"
                onClick={callContact}
              >
                Talk to Care Team
              </button>
            </div>
          </div>
        </Container>
      </section>
    </div>
  )
}

// ── inline icons ───────────────────────────────────────────

function MetaIcon({ kind }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  }
  if (kind === 'items') {
    return (
      <svg {...common}>
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" />
        <line x1="3" y1="12" x2="3.01" y2="12" />
        <line x1="3" y1="18" x2="3.01" y2="18" />
      </svg>
    )
  }
  if (kind === 'time') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    )
  }
  if (kind === 'lock') {
    return (
      <svg {...common}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    )
  }
  return (
    <svg {...common}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function Check() {
  return (
    <span className="al-check" aria-hidden="true">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  )
}

function QuoteGlyph() {
  return (
    <span className="al-quote-glyph" aria-hidden="true">“</span>
  )
}

function PaperClipIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  )
}
